import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { welcomeBannerDismissedKey } from '@/lib/user-scoped-storage';
import { cn } from '@/lib/utils';
import { COARSE_HIT_24 } from '@/lib/touch-target';

const DISMISS_TTL_MS = 30 * 864e5;

function readDismissed(userId: string): boolean {
  try {
    const at = localStorage.getItem(welcomeBannerDismissedKey(userId));
    return at ? Date.now() - Number(at) < DISMISS_TTL_MS : false;
  } catch {
    return false;
  }
}

function writeDismissed(userId: string): void {
  try {
    localStorage.setItem(welcomeBannerDismissedKey(userId), String(Date.now()));
  } catch {
    // Private window or blocked site data: the dismissal lives for this mount.
  }
}

export function WelcomeBanner() {
  const { user } = useAuth();
  const { t } = useTranslation(['onboarding', 'common']);
  const userId = user?.id ?? user?._id ?? '';
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  // Shown to all signed-in users (incl. applicants) — the Terms / Privacy / AI
  // Use links are important consent context during the apply flow too.
  if (!user || !userId) return null;
  if (dismissedFor === userId || readDismissed(userId)) return null;
  const createdAt = user.created_at ? new Date(user.created_at).getTime() : 0;
  if (createdAt && Date.now() - createdAt > 7 * 864e5) return null;
  return (
    <div className="welcome-banner isolate" role="note">
      {/* The sentence is one flex item so its inline links + punctuation flow as
          normal text — without this span the banner's `display:flex; gap` turns
          every word/link/comma into a separate, gap-spaced flex item. */}
      <span>
        <Trans
          t={t}
          i18nKey="welcome.body"
          components={{
            terms: <Link to="/legal/terms" />,
            privacy: <Link to="/legal/privacy" />,
            ai: <Link to="/legal/ai" />,
          }}
        />
      </span>
      <Link to="/legal" className="welcome-banner__review">{t('welcome.review')}</Link>
      {/* #2603 (D-2420): the x draws in a 24 px box and reaches 44 px on a
          coarse pointer; the -z-10 hit area sits under the Review link, an
          in-flow box inside this `isolate` banner. */}
      <button
        type="button"
        aria-label={t('common:actions.dismiss')}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center',
          COARSE_HIT_24,
        )}
        onClick={() => { writeDismissed(userId); setDismissedFor(userId); }}
      >
        ×
      </button>
    </div>
  );
}
